import { memo } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { format } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { cn } from '@/lib/utils'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { filterIntermediateStatuses } from '@/utils/meta-activity-filter-service'

// Import tooltip components
import BudgetChangeTooltip from './tooltips/BudgetChangeTooltip'
import StatusChangeTooltip from './tooltips/StatusChangeTooltip'
import ApprovalChangeTooltip from './tooltips/ApprovalChangeTooltip'
import CreationTooltip from './tooltips/CreationTooltip'
import TargetingUpdateTooltip from './tooltips/TargetingUpdateTooltip'
import AdUpdateTooltip from './tooltips/AdUpdateTooltip'
import NameChangeTooltip from './tooltips/NameChangeTooltip'

// Import all action icons
import {
    CampaignCreatedIcon,
    CampaignTurnedOnIcon,
    CampaignTurnedOffIcon,
    CampaignNameChangedIcon,
    AdSetCreatedIcon,
    AdSetBudgetIncreasedIcon,
    AdSetBudgetDecreasedIcon,
    AdSetTurnedOnIcon,
    AdSetTurnedOffIcon,
    AdSetTargetingUpdatedIcon,
    AdCreatedIcon,
    AdApprovedIcon,
    AdDeclinedIcon,
    AdUpdatedIcon,
    AdTurnedOnIcon,
    AdTurnedOffIcon,
    SystemOriginIcon,
    UserOriginIcon,
    MetaOriginIcon,
    ChangeIcon,
} from '@/components/icons/actions'

// Helper function to convert timezone offset to IANA timezone format
const getTimezoneFromOffset = (offset) => {
    // Note: Etc/GMT timezones have inverted signs
    // UTC+10 becomes Etc/GMT-10
    const sign = offset >= 0 ? '-' : '+'
    const absOffset = Math.abs(offset)
    return `Etc/GMT${sign}${absOffset}`
}

// Map action types to icon components
const ACTION_ICONS = {
    // Campaign actions
    campaign_created: CampaignCreatedIcon,
    campaign_turned_on: CampaignTurnedOnIcon,
    campaign_turned_off: CampaignTurnedOffIcon,
    campaign_name_changed: CampaignNameChangedIcon,

    // AdSet actions
    adset_created: AdSetCreatedIcon,
    adset_budget_increased: AdSetBudgetIncreasedIcon,
    adset_budget_decreased: AdSetBudgetDecreasedIcon,
    adset_turned_on: AdSetTurnedOnIcon,
    adset_turned_off: AdSetTurnedOffIcon,
    adset_targeting_updated: AdSetTargetingUpdatedIcon,

    // Ad actions
    ad_created: AdCreatedIcon,
    ad_approved: AdApprovedIcon,
    ad_declined: AdDeclinedIcon,
    ad_updated: AdUpdatedIcon,
    ad_name_changed: AdUpdatedIcon, // Use same icon as ad_updated
    ad_turned_on: AdTurnedOnIcon,
    ad_turned_off: AdTurnedOffIcon,
}

// Map Meta event types to our action types (simple mappings)
const META_EVENT_TYPE_MAPPING = {
    // Campaign events
    create_campaign_group: 'campaign_created',
    create_campaign_legacy: 'campaign_created',
    update_campaign_name: 'campaign_name_changed',

    // AdSet events
    create_ad_set: 'adset_created',
    update_ad_set_target_spec: 'adset_targeting_updated',

    // Ad events
    create_ad: 'ad_created',
    ad_review_approved: 'ad_approved',
    ad_review_declined: 'ad_declined',
    update_ad_creative: 'ad_updated',
    edit_and_update_ad_creative: 'ad_updated',
    update_ad_friendly_name: 'ad_name_changed',
    update_ad_run_status_to_be_set_after_review: 'ad_status_change',
}

// Smart mapping function that parses extra_data for complex events
const getSmartActionType = (eventType, extraData) => {
    try {
        const parsed = extraData ? JSON.parse(extraData) : null

        switch (eventType) {
            case 'update_campaign_run_status':
                if (parsed?.run_status?.new_value === 1) {
                    return 'campaign_turned_on'
                } else if (parsed?.run_status?.new_value === 15) {
                    return 'campaign_turned_off'
                }
                return 'campaign_turned_on' // Default fallback

            case 'update_ad_set_run_status':
                if (parsed?.run_status?.new_value === 1) {
                    return 'adset_turned_on'
                } else if (parsed?.run_status?.new_value === 7) {
                    return 'adset_turned_off'
                }
                return 'adset_turned_on' // Default fallback

            case 'update_ad_run_status_to_be_set_after_review':
                if (parsed?.new_value === 'Active') {
                    return 'ad_turned_on'
                } else if (parsed?.new_value === 'Inactive') {
                    return 'ad_turned_off'
                }
                // Fallback: compare old_value vs new_value
                return parsed?.old_value === 'Active' ? 'ad_turned_off' : 'ad_turned_on'

            case 'update_ad_run_status':
                if (parsed?.run_status?.new_value === 1) {
                    return 'ad_turned_on'
                } else if (parsed?.run_status?.new_value === 8) {
                    return 'ad_turned_off'
                }
                return 'ad_turned_on' // Default fallback

            case 'update_ad_set_budget':
                const oldBudget = parsed?.old_value?.old_value
                const newBudget = parsed?.new_value?.new_value
                if (oldBudget != null && newBudget != null) {
                    return newBudget > oldBudget ? 'adset_budget_increased' : 'adset_budget_decreased'
                }
                return 'adset_budget_increased' // Default fallback

            default:
                return META_EVENT_TYPE_MAPPING[eventType] || eventType
        }
    } catch (error) {
        console.warn('Error parsing extra_data for event:', eventType, error)
        return META_EVENT_TYPE_MAPPING[eventType] || eventType
    }
}

// Get human-readable action description for tooltips
const getActionDescription = (actionType, eventType, extraData) => {
    const actionDescriptions = {
        campaign_created: 'Campaign created',
        campaign_turned_on: 'Campaign turned on',
        campaign_turned_off: 'Campaign turned off',
        campaign_name_changed: 'Campaign name changed',
        adset_created: 'Ad set created',
        adset_budget_increased: 'Budget increased',
        adset_budget_decreased: 'Budget decreased',
        adset_turned_on: 'Ad set turned on',
        adset_turned_off: 'Ad set turned off',
        adset_targeting_updated: 'Targeting updated',
        ad_created: 'Ad created',
        ad_approved: 'Ad approved',
        ad_declined: 'Ad declined',
        ad_updated: 'Ad updated',
        ad_name_changed: 'Ad name changed',
        ad_turned_on: 'Ad turned on',
        ad_turned_off: 'Ad turned off',
    }

    return actionDescriptions[actionType] || eventType
}

// Get budget details from extra_data
const getBudgetDetails = (extraData) => {
    try {
        const parsed = extraData ? JSON.parse(extraData) : null
        if (parsed?.old_value?.old_value && parsed?.new_value?.new_value) {
            const oldAmount = (parsed.old_value.old_value / 100).toFixed(2)
            const newAmount = (parsed.new_value.new_value / 100).toFixed(2)
            const currency = parsed.old_value.currency || 'AUD'

            return {
                oldAmount,
                newAmount,
                currency,
            }
        }
    } catch (error) {
        console.warn('Error parsing budget details:', error)
    }
    return null
}

// Map Meta status labels to user-friendly labels
const mapStatusLabel = (status, entityType) => {
    // Handle null/undefined
    if (!status) return status

    // For campaigns: "Inactive" → "Paused", "Pending process" → "Active" (intermediate state)
    // For ad sets and ads: "Inactive" → "Paused"
    if (entityType === 'campaign') {
        const campaignStatusMap = {
            Active: 'Active',
            Inactive: 'Paused',
            'Pending process': 'Active', // Intermediate state during Active ↔ Paused transitions
            Paused: 'Paused',
        }
        return campaignStatusMap[status] || status
    }

    if (entityType === 'adset' || entityType === 'ad') {
        const statusMap = {
            Active: 'Active',
            Inactive: 'Paused',
            Paused: 'Paused',
        }
        return statusMap[status] || status
    }

    // Fallback: return as-is
    return status
}

// Get status change details from extra_data
const getStatusDetails = (extraData, actionType) => {
    try {
        const parsed = extraData ? JSON.parse(extraData) : null
        if (parsed?.old_value && parsed?.new_value) {
            // Determine entity type from action type
            const entityType = actionType.includes('campaign')
                ? 'campaign'
                : actionType.includes('adset')
                  ? 'adset'
                  : 'ad'

            return {
                oldStatus: mapStatusLabel(parsed.old_value, entityType),
                newStatus: mapStatusLabel(parsed.new_value, entityType),
            }
        }
    } catch (error) {
        console.warn('Error parsing status details:', error)
    }
    return null
}

const LatestActionsCell = memo(({ actions = [], className = '' }) => {
    const { connectedPublishers } = useMediaBuyingContext()

    if (!actions || actions.length === 0) {
        return <div className={`tw:p-4 tw:pl-0 tw:min-w-[180px] tw:text-sm tw:text-gray-400 ${className}`}></div>
    }

    // Filter intermediate status transitions, then take up to 5 most recent actions
    // and reverse to show chronological order (oldest to newest, left to right)
    const filteredActions = filterIntermediateStatuses(actions)
    const recentActions = filteredActions.slice(0, 5).reverse()

    // Get timezone offset from first connected publisher
    // timezone_offset is in hours (e.g., +10 for UTC+10, -5 for UTC-5)
    const publisherId = connectedPublishers ? Object.keys(connectedPublishers)[0] : null
    const timezoneOffset = publisherId ? connectedPublishers[publisherId]?.timezone_offset || 0 : 0

    return (
        <div className={`tw:p-4 tw:pl-0 tw:min-w-[180px] tw:flex tw:items-center tw:gap-1 ${className}`}>
            {recentActions.map((action, index) => {
                const mappedActionType = getSmartActionType(action.event_type, action.extra_data)
                const IconComponent = ACTION_ICONS[mappedActionType]

                // Determine origin icon based on actor type
                const OriginIcon =
                    action.origin === 'meta'
                        ? MetaOriginIcon
                        : action.origin === 'system'
                          ? SystemOriginIcon
                          : UserOriginIcon
                if (!IconComponent) {
                    console.log('IconComponent not found for action:', action)
                    return null
                }

                // Format timestamp for tooltip - calculate both user time and ad account time
                let userTime = 'Unknown time'
                let adAccountTime = 'Unknown time'

                if (action.event_time) {
                    const eventDate = new Date(action.event_time)

                    // Validate the parsed date
                    if (isNaN(eventDate.getTime())) {
                        console.warn('Invalid event_time format:', action.event_time)
                        userTime = 'Invalid time'
                        adAccountTime = 'Invalid time'
                    } else {
                        // User time (browser timezone)
                        userTime = format(eventDate, 'dd/MM/yyyy H:mm')

                        // Ad account time (publisher timezone) - always convert, even for UTC (offset 0)
                        try {
                            let accountTimezone
                            if (timezoneOffset === 0) {
                                // Ad account is in UTC
                                accountTimezone = 'UTC'
                            } else {
                                // Convert offset to IANA timezone format
                                // Note: Assuming timezone_offset is in hours (as used in useReviewLaunch.jsx)
                                accountTimezone = getTimezoneFromOffset(timezoneOffset)
                            }

                            const eventInAccountTz = toZonedTime(eventDate, accountTimezone)
                            adAccountTime = format(eventInAccountTz, 'dd/MM/yyyy H:mm')
                        } catch (error) {
                            console.warn('Error converting to ad account timezone:', error, {
                                timezoneOffset,
                                event_time: action.event_time,
                            })
                            adAccountTime = userTime // Fallback to user time
                        }
                    }
                }

                // Get action description and change details
                const actionDescription = getActionDescription(mappedActionType, action.event_type, action.extra_data)
                const budgetDetails = getBudgetDetails(action.extra_data)
                const statusDetails = getStatusDetails(action.extra_data, mappedActionType)

                // Determine if this is a status change action
                const isStatusChange = mappedActionType.includes('turned_on') || mappedActionType.includes('turned_off')

                // Determine if this is an approval change action
                const isApprovalChange = mappedActionType === 'ad_approved' || mappedActionType === 'ad_declined'

                // Determine if this is a creation action
                const isCreationAction =
                    mappedActionType === 'campaign_created' ||
                    mappedActionType === 'adset_created' ||
                    mappedActionType === 'ad_created'

                // Determine if this is a targeting update action
                const isTargetingUpdate = mappedActionType === 'adset_targeting_updated'

                // Determine if this is an ad update action
                const isAdUpdate = mappedActionType === 'ad_updated'

                // Determine if this is a name change action
                const isNameChange =
                    mappedActionType === 'campaign_name_changed' || mappedActionType === 'ad_name_changed'

                // Generate tooltip content
                const tooltipContent = budgetDetails ? (
                    <BudgetChangeTooltip
                        type={mappedActionType}
                        action={action}
                        budgetDetails={budgetDetails}
                        userTime={userTime}
                        adAccountTime={adAccountTime}
                        originIcon={OriginIcon}
                        actionIcon={IconComponent}
                    />
                ) : isStatusChange ? (
                    <StatusChangeTooltip
                        type={mappedActionType}
                        action={action}
                        userTime={userTime}
                        adAccountTime={adAccountTime}
                        originIcon={OriginIcon}
                        actionIcon={IconComponent}
                        oldStatus={statusDetails?.oldStatus}
                        newStatus={statusDetails?.newStatus}
                    />
                ) : isApprovalChange ? (
                    <ApprovalChangeTooltip
                        type={mappedActionType}
                        action={action}
                        userTime={userTime}
                        adAccountTime={adAccountTime}
                        originIcon={OriginIcon}
                        actionIcon={IconComponent}
                    />
                ) : isCreationAction ? (
                    <CreationTooltip
                        type={mappedActionType}
                        action={action}
                        userTime={userTime}
                        adAccountTime={adAccountTime}
                        originIcon={OriginIcon}
                        actionIcon={IconComponent}
                    />
                ) : isTargetingUpdate ? (
                    <TargetingUpdateTooltip
                        type={mappedActionType}
                        action={action}
                        userTime={userTime}
                        adAccountTime={adAccountTime}
                        originIcon={OriginIcon}
                        actionIcon={IconComponent}
                    />
                ) : isAdUpdate ? (
                    <AdUpdateTooltip
                        type={mappedActionType}
                        action={action}
                        userTime={userTime}
                        adAccountTime={adAccountTime}
                        originIcon={OriginIcon}
                        actionIcon={IconComponent}
                    />
                ) : isNameChange ? (
                    <NameChangeTooltip
                        type={mappedActionType}
                        action={action}
                        userTime={userTime}
                        adAccountTime={adAccountTime}
                        originIcon={OriginIcon}
                        actionIcon={IconComponent}
                    />
                ) : (
                    <div className="tw:text-left tw:space-y-1">
                        {/* Action Description Header */}
                        <div className="tw:font-medium tw:text-sm tw:text-white">{actionDescription}</div>

                        {/* Object Name */}
                        <div className="tw:text-sm tw:text-gray-200">{action.object_name || 'Unknown object'}</div>

                        {/* Budget Details (if available) */}
                        {budgetDetails && (
                            <div className="tw:text-xs tw:text-gray-300 tw:flex tw:items-center tw:gap-1">
                                <span>
                                    {budgetDetails.currency} {budgetDetails.oldAmount}
                                </span>
                                <ChangeIcon size={12} />
                                <span>
                                    {budgetDetails.currency} {budgetDetails.newAmount}
                                </span>
                            </div>
                        )}

                        {/* Status Change Details (if available) */}
                        {isStatusChange && statusDetails && (
                            <div className="tw:text-xs tw:text-gray-300 tw:flex tw:items-center tw:gap-1">
                                <span>{statusDetails.oldStatus}</span>
                                <ChangeIcon size={12} />
                                <span>{statusDetails.newStatus}</span>
                            </div>
                        )}

                        {/* Actor */}
                        {action.actor_name && <div className="tw:text-xs tw:text-gray-300">by {action.actor_name}</div>}

                        {/* Timestamps */}
                        <div className="tw:flex tw:justify-between tw:gap-6">
                            <div className="tw:flex tw:flex-col tw:gap-1">
                                <span className="tw:text-xs tw:font-semibold tw:text-gray-400">User Time</span>
                                <span className="tw:text-xs tw:text-gray-400">{userTime}</span>
                            </div>
                            <div className="tw:flex tw:flex-col tw:gap-1">
                                <span className="tw:text-xs tw:font-semibold tw:text-gray-400">Ad Account Time</span>
                                <span className="tw:text-xs tw:text-gray-400">{adAccountTime}</span>
                            </div>
                        </div>

                        {/* Origin */}
                        <div className="tw:text-xs tw:text-gray-400">
                            {action.origin === 'meta'
                                ? 'Meta Platform'
                                : action.origin === 'system'
                                  ? 'Turba App'
                                  : 'Manual Action'}
                        </div>
                    </div>
                )

                return (
                    <Tooltip key={`${action.event_type}-${index}`}>
                        <TooltipTrigger asChild>
                            <div className="tw:relative tw:cursor-help tw:inline-block">
                                <IconComponent size={27} />
                                {/* Origin indicator positioned at bottom-right corner */}
                                <div className="tw:absolute tw:bottom-0 tw:right-0 tw:translate-x-1/4 tw:translate-y-1/4">
                                    <OriginIcon size={10} />
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent
                            className={
                                budgetDetails ||
                                isStatusChange ||
                                isApprovalChange ||
                                isCreationAction ||
                                isTargetingUpdate ||
                                isAdUpdate ||
                                isNameChange
                                    ? '!tw:bg-transparent !tw:border-none !tw:shadow-none !tw:px-0 !tw:py-0 !tw:backdrop-blur-0'
                                    : undefined
                            }
                            showArrow={
                                !budgetDetails &&
                                !isStatusChange &&
                                !isApprovalChange &&
                                !isCreationAction &&
                                !isTargetingUpdate &&
                                !isAdUpdate &&
                                !isNameChange
                            }
                        >
                            {tooltipContent}
                        </TooltipContent>
                    </Tooltip>
                )
            })}
        </div>
    )
})

LatestActionsCell.displayName = 'LatestActionsCell'

export default LatestActionsCell
